export interface EventsAccordionProps {
    event: any;
    selectedChild: any;
    handleCartDrawer: any;
}

export enum RegistrationStatus {
  OPEN = 'Open',
  CLOSED = 'Closed',
  REGISTERED = 'Registered',
  OPENING_SOON = 'Opening Soon',
}

export enum EventFormSubmissionStatus {
  DELETED = 0,
  DRAFT = 1,
  PENDING_APPROVAL = 2,
  SUBMITTED = 3,
}

export interface EventsCardProps {
    event: any;
    onRegisterClick: (event: any) => void;
    selectedChildId?: number;
}

export interface ExpandedAccordionsType {
    [key: string]: boolean;
}

export interface PricingDetail {
    id: number;
    eventId: number;
    description: string;
    startTime: string;
    endTime: string;
    chargeType: number;
    chargePerPeriod: string;
    chargePerPeriodSub: string;
    status: number;
    maxScheduleDays: number;
    isSelectScheduleAllowed: boolean;
    isDepositRequired: boolean;
    depositAmount: string;
    autoPaymentDate: string;
    sun: boolean;
    mon: boolean;
    tue: boolean;
    wed: boolean;
    thu: boolean;
    fri: boolean;
    sat: boolean;
    createdAt: string;
    updatedAt: string;
    discountAmount: number;
    discountType: number;
    /** Optional active period start (ISO timestamp). If unset, tier is active for event lifetime. */
    activeStart?: string | null;
    /** Optional active period end (ISO timestamp). If unset with activeStart, tier is active for event lifetime. */
    activeEnd?: string | null;
}

/** Optional event form attached to an event (from Event Details API). */
export interface EventFormInfo {
  id: number;
  name: string;
  description?: string;
  /** JSON string defining the form schema (forms.io format). */
  formSchema: string;
  approvalRequired?: boolean;
}

export interface EventDetail {
  isRecurring: number;
  locationName: string;
  isVisible: number;
  id: number;
  name: string;
  description: string;
  /** Plain-text summary for listing cards (max 120 chars). Optional for legacy events. */
  shortDescription?: string | null;
  images: { signedUrl: string; star?: number; order?: number }[];
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  nextStartDate: string | null;
  minMonthsRestrictions: number;
  capacity: number;
  occupiedSeats: number;
  totalOccupiedSeats: number;
  status: number;
  pricing: PricingDetail[];
  isWaitlistEnabled: number;
  locationId: number;
  location?: {
    id: number;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    countryCode: string;
    postal: string;
    region: string;
    address2: string;
  };
  categoryId: number;
  category?: {
    id: number;
    name: string;
  };
  /** Optional event form to complete for this event. */
  eventForm?: EventFormInfo;
  /** IANA timezone for the event's location (e.g. America/Toronto). Used for active period comparisons. */
  timezone?: string;
  programEnrollments: [
    {
      id: number;
      childId: number;
      startDate: string;
      endDate: string;
      status: number;
    },
  ];
  createdAt: string;
  updatedAt: string;
}

export interface CartSummaryProps {
    applyCouponCode?: (couponCode: string) => Promise<boolean>;
    readonly: boolean;
    checkout: boolean;
    showPayInFullText?: boolean;
    onPayDeposit?: () => void;
    onPayFull?: () => void;
    buttonDisabled?: boolean;
}

export interface BillingFormProps {
  billingInfo: {
    savePreferred: boolean;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phone: string;
    email: string;
  };
  setBillingInfo: React.Dispatch<
    React.SetStateAction<{
      savePreferred: boolean;
      firstName: string;
      lastName: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      phone: string;
      email: string;
    }>
  >;
  handleNext: (stripeData: { token: string; brand: string; tokenPreferred: string }) => void;
}

export interface ChildrenAccordionProps {
  id?: number;
  fname: string;
  lname: string;
  dob: string;
  gender: string;
  genders: string[];
  isNew: boolean;
  onDelete: () => void;
  onUpdate?: (childId: number, updates: { fname?: string; lname?: string; dob?: string }) => void;
}

export interface ConfirmDialogProps {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  title: string;
  description: string;
}

export interface SnackbarProps {
  open: boolean;
  message: string;
  severity: string;
  handleClose: () => void;
  autoHideDuration?: number;
  linkLabel?: string;
  linkOnClick?: () => void;
}

/** Variants for standardized on-page confirmations (replaces toast for key actions). */
export type ActionConfirmationVariant = 'success' | 'error' | 'info' | 'warning';

/**
 * Inline confirmation rendered near the source of the action.
 * Copy guidelines: success = "Action completed."; error = "What went wrong."; keep concise.
 */
export interface ActionConfirmationProps {
  /** Whether the confirmation is visible. */
  visible: boolean;
  /** success | error | info | warning — determines styling and ARIA live region. */
  variant: ActionConfirmationVariant;
  /** Short, clear message (plain text; no HTML). */
  message: string;
  /** Called when user dismisses or auto-hide runs. */
  onDismiss: () => void;
  /** Optional: id for the confirmation element (for aria-describedby from trigger). */
  id?: string;
  /** Auto-dismiss after ms (0 = no auto-dismiss). Default 6000. */
  autoHideDuration?: number;
  /** Optional: show dismiss button. Default true for inline. */
  dismissible?: boolean;
  /** Optional: link label (e.g. "View order") — shown below message. */
  linkLabel?: string;
  /** Optional: called when link is clicked. */
  linkOnClick?: () => void;
}

/**
 * Modal confirmation for critical outcomes (e.g. purchase complete).
 * Uses Dialog with focus trap and return-focus on close.
 */
export interface ActionConfirmationModalProps extends Omit<ActionConfirmationProps, 'dismissible'> {
  /** Optional label for the dialog (for screen readers). Default from variant. */
  title?: string;
}

export interface TextMaskCustomProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export interface ParentFormProps {
  title: string;
  subtitle?: string;
  disableInputs?: boolean;
  onParentDataChange?: (data: any) => void;
  onPasswordChange?: (password: string) => void;
}

export interface PayerInfoProps {
  billingInfo: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface RegisterButtonProps {
  selectedEventGroup: any;
  openModal: () => void;
}

export interface ErrorResponse {
  message: string;
}

export interface CartResponse {
  statusCode: number;
  isSuccess: boolean;
  data: {
    data: CartItem[];
    meta: {
      itemsPerPage: number;
      totalItems: number;
      currentPage: number;
      totalPages: number;
      sortBy: Array<[string, string]>;
      filter: {
        cartId: string;
      };
    };
    links: {
      current: string;
    };
    coupons: Coupon[];
  };
  invalidItems: CartItem[];
  invalidCoupons: Coupon[];
}

export interface PaymentMethodsResponse {
  statusCode: number;
  isSuccess: boolean;
  data: PaymentMethodParams;
}

export interface PaymentMethodParams {
  cardType: number;
  cardNumber: string;
  cardExpire: string;
  paymentType: number;
}

export enum PaymentMethodType {
  //ACH = 1,
  BANK = 1,
  VISA = 2,
  MASTERCARD = 3,
  AMEX = 4,
  DISCOVER = 5,
  JCB = 6,
  DINERS = 7,
  UNIONPAY = 8,
  DEFAULT = 0,
}

export enum OrderPaymentType {
  FULL = 1,
  DEPOSIT = 2,
}

export interface ProcessingFeeResponse {
  statusCode: number;
  isSuccess: boolean;
  data: {
    /** New normalized processing fee payload. */
    processingFee?: {
      baseAmount: number;
      percentage: number;
      fixedAmount: number;
      amount: number;
      description: string;
    };
    /** Optional normalized totals from backend. */
    dueTodayAfterDiscounts?: number;
    totalDueToday?: number;
    paymentSummary?: PaymentSummary;
    /** Backward-compatible legacy fields. */
    description?: string;
    percentage?: number;
    amount?: number;
  };
}

export interface PaymentSummary {
  orderPaymentType: 'deposit' | 'full';
  initialTotal: number;
  depositsAmount?: number;
  fullPaymentItemsAmount?: number;
  couponDiscountTotal: number;
  paidInFullDiscountTotal: number;
  dueTodayAfterDiscounts: number;
  remainingBalance: number;
  chargeDates?: string[];
  processingFee: {
    baseAmount: number;
    percentage: number;
    fixedAmount: number;
    amount: number;
    description: string;
  };
  totalDueToday: number;
  totalPaidTodayWithFee: number;
  totalPurchaseAmount: number;
  chargedOn: string;
}

export interface CartItem {
  discountAmountCoupon: number;
  reservedSeats: number;
  invalidMessage: string;
  id: number;
  child: {
    id: number;
    firstName: string;
    lastName: string;
  };
  item: {
    id: number;
    chargeType: number;
    chargePerPeriod: string;
    status: number;
    isDepositRequired: boolean;
    depositAmount: string;
    autoPaymentDate: string;
    event: {
      id: number;
      name: string;
      status: number;
      capacity: number;
      occupiedSeats: number;
      startDate: string;
      endDate: string;
    };
  };
}

export interface Coupon {
  invalidMessage: string;
  id: string;
  coupon: {
    locationId: number;
    location: {
      id: number;
      name: string;
    };
    id: number;
    code: string;
    amount: number;
    status: number;
    type: number;
  };
}

export interface CartState {
  data: CartItem[];
  invalidItems: CartItem[];
  invalidCoupons: Coupon[];
  meta: {
    itemsPerPage: number;
    totalItems: number;
    currentPage: number;
    totalPages: number;
    sortBy: Array<[string, string]>;
    filter: {
      cartId: string;
    };
  };
  links: {
    current: string;
  };
  coupons: Coupon[];
}

export interface OrderResponse {
  statusCode: number;
  isSuccess: boolean;
  data: {
    order: {
      uuid: string;
      total: number;
      depositTotal: number;
      status: number;
      items: OrderItem[];
      createdAt: string;
      updatedAt: string;
    };
    invalidMessages: string[];
    cartMessages: string[];
  };
}

export interface OrderItem {
  amount: string;
  depositAmount: string;
  id: number;
  createdAt: string;
  updatedAt: string;
}

export interface CompleteOrderResponse {
  statusCode: number;
  isSuccess: boolean;
  data: {
    id: string;
    last4: number;
    enrollmentDetails: EnrollmentDetail[];
  };
}

export interface EnrollmentDetail {
  childId: number;
  startDate: string;
}

/** Snapshot of a completed sale for the Thank You / Completed Sale page. Loaded from GET /orders/:uuid API. */
export interface OrderScheduleOccurrence {
  date: string;
  startTime: string;
  endTime: string;
}

export interface OrderScheduleItem {
  eventUuid: string;
  eventName: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  expectedStartDate: string;
  expectedEndDate: string;
  timezone: string;
  occurrences: OrderScheduleOccurrence[];
  childName: string;
}

export interface OrderScheduleResponse {
  statusCode: number;
  isSuccess: boolean;
  data: OrderScheduleItem[];
}

export interface CompletedSaleRegistration {
  eventName: string;
  childName: string;
  childId?: number;
  /** Full item price before deposit split. */
  itemPrice?: string;
  /** Amount charged today for this item (deposit or full). */
  paidToday?: string;
  /** Remaining amount for this item when paid by deposit. */
  remainingForItem?: string;
  amountPaid: string;
  eventStartDate: string;
  eventEndDate: string;
  autoPaymentDate?: string;
  /** Populated after purchase when order schedule is fetched for add-to-calendar. */
  occurrences?: OrderScheduleOccurrence[];
  /** Event/location timezone as returned by schedule API; required for calendar export. */
  timezone?: string;
  /** Event uuid from schedule API (if available). */
  eventUuid?: string;
}

export interface CompletedSaleSnapshot {
  orderType: OrderPaymentType;
  totalPaidToday: number;
  depositPaid?: number;
  remainingBalance: number;
  totalPurchaseAmount: number;
  /** Subtotal before coupon (sum of item amounts). For display with coupon line. */
  totalBeforeCoupon?: number;
  /** Due today before coupon: deposits + full-payment item amounts. Used as base for processing fee to match checkout. */
  dueTodayBeforeCoupon?: number;
  /** Total coupon discount applied (totalBeforeCoupon - totalPurchaseAmount). */
  couponDiscountTotal?: number;
  processingFee: number;
  processingFeeName: string;
  payInFullDiscount?: number;
  registrations: CompletedSaleRegistration[];
  /** Earliest scheduled auto-payment date when purchase was with deposit; for "What's next" notice. */
  scheduledAutoPaymentDate?: string;
  /** When true, show "Complete your forms" CTA; can be set from API when required event forms exist. */
  hasOutstandingForms: boolean;
  depositTotal?: number;
}

/**
 * Raw outstanding form item as returned by the API (GET outstanding forms).
 */
export interface OutstandingFormItem {
  orderId: number;
  orderUuid: string;
  eventId: number;
  eventName: string;
  formId: number;
  templateName: string;
  childId: number;
  childName: string;
  /** Form schema JSON string (Form.io). */
  form: string;
  /** Optional event UUID when API provides it (e.g. for submit). */
  eventUuid?: string;
}

/**
 * Remaining required event form associated with the logged-in parent's
 * family/children (UI-facing shape).
 */
export interface RequiredEventForm {
  /** Form template id from API. */
  id: number;
  /** Human-readable form name, e.g. "Medical Consent Form". */
  formName: string;
  /** Event name the form is tied to. */
  eventName: string;
  /** Whether this required form has been completed/submitted. */
  completed?: boolean;
  /** ISO date string when the form was completed/submitted. */
  completedAt?: string;
  /** Submission id (forms_api) for viewing completed form. */
  submissionId?: number;
  /** Optional child id when the form is for a specific child (for submittedForChilds). */
  childId?: number;
  /** Optional child name for per-child forms. */
  childName?: string;
  /** Optional due date string (ISO) if backend provides it. */
  dueDate?: string;
  /** Optional URL if the form is hosted on a separate page. */
  formUrl?: string;
  /** Event ID the form is tied to for de-duplication in case forms are shared across events. **/
  eventId?: number;
  /** Event UUID for fetching form schema from /offline/events/:uuid when formUrl is not provided. */
  eventUuid?: string;
  /** Inline form schema JSON string when API returns it (avoids extra fetch). */
  form?: string;
}

/** API response shape for remaining required event forms (To Do list). */
export interface OutstandingFormsResponse {
  statusCode: number;
  isSuccess: boolean;
  data: {
    hasOutstandingForms: boolean;
    outstandingForms: OutstandingFormItem[];
    remainingCount: number;
  };
}

/** Event setting (e.g. waiver content) returned with waiver items. */
export interface EventWaiverSetting {
  id: number;
  data: string;
  status: number;
  type: number;
  locationId: number;
  primary: number;
  createdAt: string;
  updatedAt: string;
}

/** Waiver item from GET events/waivers. */
export interface EventWaiverItem {
  childId: number;
  childName: string;
  templateName: string;
  completed: boolean;
  /** ISO date string when the waiver was completed/signed. */
  completedAt?: string;
  eventSetting: EventWaiverSetting;
}

/** API response shape for GET events/waivers. */
export interface EventWaiversResponse {
  statusCode: number;
  isSuccess: boolean;
  data: EventWaiverItem[];
}

export enum PaymentMethodEnum {
  BANK = 1,
  CARD = 2,
}

export interface CartItemType {
  cartMessages: string[];
  invalidCoupons: any[];
  invalidItems: any[];
  invalidMessages: string[];
  data: any[];
}

export enum PricingDiscountTypes {
  DISABLED = 0,
  FIXED = 1,
  PERCENTAGE = 2,
}

export interface Child {
  id: number;
  fname: string;
  lname: string;
  dob: string;
  gender: string;
  isNew: boolean;
}

export interface OrderItemData {
  id: number;
  child: {
    id: number;
    firstName: string;
    lastName: string;
  };
  item: {
    id: number;
    chargePerPeriod: string;
    chargeType: number;
    isDepositRequired: boolean;
    depositAmount: string;
    autoPaymentDate: string;
    event: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
      status: number;
      capacity: number;
      occupiedSeats: number;
    };
  };
  discountAmountCoupon: number;
  reservedSeats: number;
}

export interface EventRegistrationWithPricing {
  eventId: number;
  eventName: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Upcoming' | 'Completed';
  pricing: {
    amountPaid: string;
    totalCost: string;
    balanceOwed: string;
    isPaidInFull: boolean;
    isDepositPaid: boolean;
    nextPaymentDate: string | null;
  };
}

export interface User {
  uid: string;
  fname: string;
  lname: string;
  email: string;
  phone: string;
  payerId: number;
  children: Child[];
  selectedChild?: Child;
}

export interface InvoiceOrderItem {
  id: number;
  amount: string;
  depositAmount: string;
  createdAt: string;
  order: {
    id: number;
    uuid: string;
    status: number;
    total: string;
    depositTotal: string;
    createdAt: string;
  };
  item: {
    id: number;
    description: string;
    chargePerPeriod: string;
    isDepositRequired: boolean;
    depositAmount: string;
    autoPaymentDate: string;
    event: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
    };
  };
}

export interface ChildWithInvoices {
  id: number;
  firstName: string;
  lastName: string;
  orderItems: InvoiceOrderItem[];
}

export interface InvoiceHistoryResponse {
  statusCode: number;
  isSuccess: boolean;
  data: {
    id: number;
    email: string;
    children: ChildWithInvoices[];
  };
}

export enum OrderErrors {
  DUPLICATE_CHARGE_PREVENTED = 'DUPLICATE_CHARGE_PREVENTED',
}
